import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import FilterInDropdown from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { DEFAULT_SEASON_SLUG } from '@sorare/core/src/constants/routes';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { rivals } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SeasonInterval } from 'components/rivals/seasons/SeasonInterval';

import { SeasonEntry } from './SeasonEntry';
import {
  PastSeasonsSelectorQuery,
  PastSeasonsSelectorQueryVariables,
  SeasonSelector_footballRivalsSeason,
} from './__generated__/index.graphql';

const DropdownContent = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  background-color: var(--c-neutral-300);

  & > :not(:last-child) {
    border-bottom: 1px solid var(--c-neutral-150);
  }

  @media ${tabletAndAbove} {
    max-width: 271px;
  }
`;

const footballRivalsSeasonFragment = gql`
  fragment SeasonSelector_footballRivalsSeason on FootballRivalsSeason {
    id
    slug
    startDate
    cutOffDate
    ...SeasonEntry_footballRivalsSeason
  }
` as TypedDocumentNode<SeasonSelector_footballRivalsSeason>;
const SEASON_SELECTOR_QUERY = gql`
  query PastSeasonsSelectorQuery($userSlug: String!, $after: String) {
    football {
      rivals {
        id
        currentSeason {
          slug
          ...SeasonSelector_footballRivalsSeason
        }
        pastSeasons(after: $after, first: 10) {
          nodes {
            slug
            ...SeasonSelector_footballRivalsSeason
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${SeasonEntry.fragments.footballRivalsSeason}
  ${footballRivalsSeasonFragment}
` as TypedDocumentNode<
  PastSeasonsSelectorQuery,
  PastSeasonsSelectorQueryVariables
>;

type Props = {
  seasonSlug: string;
  userSlug: string;
  onChange: (seasonSlug: string) => void;
};
export const SeasonSelector = ({ seasonSlug, userSlug, onChange }: Props) => {
  const { data, loadMore } = usePaginatedQuery(SEASON_SELECTOR_QUERY, {
    connection: 'FootballRivalsSeasonConnection',
    variables: { userSlug },
  });

  const { currentSeason, pastSeasons } = data?.football.rivals || {};
  const { pageInfo, nodes } = pastSeasons || {};
  const seasons = [currentSeason, ...(nodes || [])].filter(Boolean);

  const { InfiniteScrollLoader } = useInfiniteScroll(() => {
    loadMore(false, {
      after: pageInfo?.endCursor,
    });
  }, !!pageInfo?.hasNextPage);

  const currentSeasonSelected =
    seasonSlug === DEFAULT_SEASON_SLUG || seasonSlug === currentSeason?.slug;
  const selectedSeason = currentSeasonSelected
    ? currentSeason
    : seasons.find(({ slug }) => slug === seasonSlug);

  return (
    <FilterInDropdown
      buttonLabel={
        currentSeasonSelected ? (
          <LabelM bold>
            <FormattedMessage {...rivals.currentSeason} />
          </LabelM>
        ) : (
          selectedSeason && (
            <SeasonInterval
              startDate={selectedSeason.startDate}
              endDate={selectedSeason.cutOffDate}
              noBackground
            />
          )
        )
      }
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {seasons.map((season, i) => (
            <SeasonEntry
              key={season.slug}
              i={i}
              season={season}
              onClick={() => {
                onChange(season.slug);
                closeDropdown();
              }}
              selected={season.slug === seasonSlug}
            />
          ))}
          <InfiniteScrollLoader />
        </DropdownContent>
      )}
    </FilterInDropdown>
  );
};
